import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { BuyerService } from '../../shared/services/buyer/buyer.service';
import { BuyerConfigV2Service } from './buyer-config-settings-v2.service';

import { SingleBuyer } from '../config-settings/config-settings.interface';

import {
  AidaV2Config,
  BuyerConfigV2ApiResponse,
} from './buyer-config-settings-v2.interface';

@Component({
  selector: 'ps-buyer-config-settings-v2',
  templateUrl: './buyer-config-settings-v2.component.html',
  styleUrls: ['./buyer-config-settings-v2.component.css'],
})
export class BuyerConfigSettingsV2Component implements OnInit, OnDestroy {
  private _subscriptions = new Subscription();

  public buyers: SingleBuyer[] = [];
  public selectedBuyer: SingleBuyer | null = null;

  public loadingBuyers = false;
  public loadingConfig = false;
  public saving = false;

  // AIDA V2 state
  public aidaEnabled = false;
  public surveysAreRace = false;
  public supplierConcentrationMitigation = false;
  public customBuyerPrompt = '';

  public showPromptModal = false;
  public error: string | null = null;

  constructor(
    private _buyerService: BuyerService,
    private _buyerConfigV2Service: BuyerConfigV2Service,
    private _toastr: ToasterService
  ) {}

  ngOnInit(): void {
    this.fetchBuyers();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  fetchBuyers(): void {
    this.loadingBuyers = true;
    const sub = this._buyerService.getBuyers().subscribe(
      (res) => {
        this.buyers = res.company || [];
        this.loadingBuyers = false;
      },
      (err) => {
        this.loadingBuyers = false;
        const msg = err?.error?.msg || 'Error while fetching buyers';
        this._toastr.error(msg);
      }
    );
    this._subscriptions.add(sub);
  }

  selectBuyer(buyer: SingleBuyer): void {
    this.selectedBuyer = buyer;
    if (buyer && buyer.id) {
      this.loadConfigForBuyer(buyer.id);
    }
  }

  loadConfigForBuyer(buyerId: number): void {
    this.loadingConfig = true;
    this.error = null;

    const sub = this._buyerConfigV2Service.getConfig(buyerId).subscribe(
      (res: BuyerConfigV2ApiResponse) => {
        const aidaV2 = (res.data?.aidaV2 ?? {}) as Partial<AidaV2Config>;

        this.aidaEnabled = !!aidaV2.enabled;
        this.surveysAreRace = !!aidaV2.surveysAreRace;
        this.supplierConcentrationMitigation =
          !!aidaV2.supplierConcentrationMitigation;
        this.customBuyerPrompt = aidaV2.customBuyerPrompt ?? '';

        this.loadingConfig = false;
      },
      (err) => {
        this.loadingConfig = false;
        const msg =
          err?.error?.msg || 'Error while fetching Buyer Config Settings V2';
        this.error = msg;
        this._toastr.error(msg);
      }
    );

    this._subscriptions.add(sub);
  }

  saveAidaV2(): void {
    if (!this.selectedBuyer) {
      return;
    }
    this.saving = true;
    this.error = null;

    const payload: AidaV2Config = {
      enabled: this.aidaEnabled,
      surveysAreRace: this.surveysAreRace,
      supplierConcentrationMitigation: this.supplierConcentrationMitigation,
      customBuyerPrompt: this.customBuyerPrompt,
    };

    const sub = this._buyerConfigV2Service
      .updateAidaV2(this.selectedBuyer.id, payload)
      .subscribe(
        () => {
          this.saving = false;
          this._toastr.success('AIDA V2 settings updated successfully');
        },
        (err) => {
          this.saving = false;
          const msg =
            err?.error?.msg || 'Error while updating AIDA V2 settings';
          this.error = msg;
          if (err?.error?.apistatus === 'warning') {
            this._toastr.warning(msg);
          } else {
            this._toastr.error(msg);
          }
        }
      );

    this._subscriptions.add(sub);
  }

  onAidaEnabledChange(value: boolean): void {
    this.aidaEnabled = value;
    if (!value) {
      this.surveysAreRace = false;
      this.supplierConcentrationMitigation = false;
    }
  }

  onSurveysAreRaceChange(value: boolean): void {
    this.surveysAreRace = value;
  }

  onSupplierConcentrationChange(value: boolean): void {
    this.supplierConcentrationMitigation = value;
  }

  // prompt
  openPromptModal(): void {
    this.showPromptModal = true;
  }

  closePromptModal(): void {
    this.showPromptModal = false;
  }

  onPromptChange(value: string): void {
    this.customBuyerPrompt = value;
  }
}
